import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Upload, Loader2, ArrowLeft, Download, 
  AlertTriangle, CheckCircle, Target, Moon, Sun, CreditCard, LogOut, User, Copy, History, Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { useTheme } from '../components/ThemeProvider';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalyzePage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, token, logout, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    // Fetch updated user info on mount
    if (token && user?.analyses_remaining === 0) {
      fetchUserInfo();
    }
    // Fetch analysis history
    fetchAnalysisHistory();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  const fetchAnalysisHistory = async () => {
    try {
      const response = await axios.get(`${API}/analyses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysisHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      setUploadProgress(10);
      const formData = new FormData();
      formData.append('file', file);

      setUploadProgress(50);
      const response = await axios.post(`${API}/upload-resume`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setUploadProgress(100);
      setResumeText(response.data.text);
      toast.success('Resume uploaded successfully');
      
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload resume');
      setUploadProgress(0);
    }
  };

  const handleAnalyze = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      toast.error('Please provide both resume and job description');
      return;
    }

    if (user?.analyses_remaining <= 0) {
      toast.error('No analyses remaining. Please purchase more.');
      navigate('/payment');
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/analyze`, {
        resume_text: resumeText,
        job_description: jobDescription
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minutes timeout
      });

      setAnalysis(response.data);
      updateUser({ analyses_remaining: (user?.analyses_remaining || 0) - 1 });
      
      // Refresh history
      fetchAnalysisHistory();
      
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again with shorter text.');
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'No analyses remaining. Please purchase more.');
        navigate('/payment');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        logout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed. Please try with shorter text or try again later.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const exportPlainText = () => {
    if (!analysis) return;
    
    const element = document.createElement('a');
    const file = new Blob([analysis.final_resume_draft], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'optimized_resume.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Plain text downloaded');
  };

  const copyToClipboard = () => {
    if (!analysis) return;
    
    navigator.clipboard.writeText(analysis.final_resume_draft).then(() => {
      toast.success('Resume copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy. Please select and copy manually.');
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-slate-500';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-orange-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-slate-700 dark:text-slate-300" />
              <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Resume Rx
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Analyses Remaining Badge */}
            <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-full px-4 py-2 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  {analysisHistory.length} used
                </span>
              </div>
              <div className="w-px h-4 bg-emerald-300 dark:bg-emerald-700"></div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  {user?.analyses_remaining || 0} left
                </span>
              </div>
            </div>

            {/* Buy More Button */}
            {(user?.analyses_remaining || 0) === 0 && (
              <Button
                data-testid="buy-more-button"
                onClick={() => navigate('/payment')}
                size="sm"
                className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-full"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Buy More
              </Button>
            )}

            {/* Theme Toggle */}
            <Button
              data-testid="theme-toggle-analyze"
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            {/* User Menu */}
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="icon"
              onClick={logout}
              className="rounded-full"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* History Sidebar */}
        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-80 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-xl z-40 overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <History className="w-5 h-5" />
                    Analysis History
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowHistory(false)}
                    className="rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Usage Stats */}
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Used</span>
                    <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                      {analysisHistory.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Remaining</span>
                    <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                      {user?.analyses_remaining || 0}
                    </span>
                  </div>
                </div>

                {/* History List */}
                <div className="space-y-3">
                  {analysisHistory.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                      No analyses yet. Start your first analysis!
                    </p>
                  ) : (
                    analysisHistory.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setAnalysis(item);
                          setShowHistory(false);
                        }}
                        className="w-full text-left bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg p-4 transition-colors"
                        data-testid={`history-item-${index}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {new Date(item.timestamp).toLocaleDateString()}
                          </span>
                          <Badge className={`${item.ats_score >= 70 ? 'bg-emerald-500' : item.ats_score >= 50 ? 'bg-yellow-500' : 'bg-orange-500'} text-white text-xs`}>
                            {item.ats_score}/100
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white line-clamp-2 mb-1">
                          {item.job_description.substring(0, 100)}...
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${showHistory ? 'ml-80' : 'ml-0'}`}>
          {/* History Toggle Button */}
          <Button
            data-testid="toggle-history-button"
            onClick={() => setShowHistory(!showHistory)}
            className="fixed left-4 top-24 z-30 bg-emerald-500 text-white hover:bg-emerald-600 rounded-full shadow-lg"
            size="icon"
          >
            {showHistory ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {!analysis ? (
          /* Input Section */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Resume Input */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Resume
              </h2>
              
              <div className="flex gap-2">
                <Button
                  data-testid="upload-pdf-button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 rounded-md px-4 py-2"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload PDF
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              {uploadProgress > 0 && (
                <Progress value={uploadProgress} className="h-2" data-testid="upload-progress" />
              )}

              <Textarea
                data-testid="resume-textarea"
                placeholder="Or paste your resume text here..."
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                className="min-h-[400px] font-mono text-sm bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl p-4"
              />
            </div>

            {/* Job Description Input */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Target Job
              </h2>
              
              <Textarea
                data-testid="job-description-textarea"
                placeholder="Paste the job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="min-h-[400px] font-mono text-sm bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl p-4"
              />

              <Button
                data-testid="analyze-button"
                onClick={handleAnalyze}
                disabled={isAnalyzing || !resumeText || !jobDescription}
                className="w-full bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg hover:shadow-xl rounded-full px-8 py-6 text-lg font-semibold"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Target className="w-5 h-5 mr-2" />
                    Analyze Resume
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        ) : (
          /* Results Section */
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* Header with Score and Export */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Analysis Results
                  </h2>
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm text-slate-600 dark:text-slate-400">ATS Score</span>
                      <div className={`text-5xl font-bold ${getScoreColor(analysis.ats_score)}`} data-testid="ats-score">
                        {analysis.ats_score}/100
                      </div>
                    </div>
                    <div className="flex-1">
                      <Progress value={analysis.ats_score} className="h-3" data-testid="ats-score-progress" />
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2" data-testid="score-rationale">
                        {analysis.score_rationale}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    data-testid="copy-button"
                    onClick={copyToClipboard}
                    className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-md px-4 py-2"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Resume
                  </Button>
                  <Button
                    data-testid="export-txt-button"
                    onClick={exportPlainText}
                    className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 rounded-md px-4 py-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download TXT
                  </Button>
                </div>
              </div>

              {/* Top Fixes */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <AlertTriangle className="w-6 h-6 text-orange-500" />
                  Top Fixes
                </h3>
                <div className="space-y-4" data-testid="top-fixes-list">
                  {analysis.top_fixes.map((fix, index) => (
                    <div key={index} className="border-l-4 pl-4" style={{ borderColor: getPriorityColor(fix.priority).replace('bg-', '#') }} data-testid={`fix-${index}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${getPriorityColor(fix.priority)} text-white`}>
                          {fix.priority}
                        </Badge>
                        <span className="font-semibold text-slate-900 dark:text-white">{fix.title}</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{fix.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Keyword Gaps */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Keyword Analysis
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="keyword-gaps-list">
                  {analysis.keyword_gaps.map((gap, index) => (
                    <div key={index} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4" data-testid={`keyword-gap-${index}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="mono text-sm font-semibold text-slate-900 dark:text-white bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                          {gap.keyword}
                        </code>
                        <Badge className={
                          gap.status === 'missing' ? 'bg-red-500' :
                          gap.status === 'weak' ? 'bg-yellow-500' : 'bg-emerald-500'
                        }>
                          {gap.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{gap.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section Notes */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Section-by-Section Review
                </h3>
                <div className="space-y-4" data-testid="section-notes-list">
                  {analysis.section_notes.map((note, index) => (
                    <div key={index} data-testid={`section-note-${index}`}>
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-2">{note.section}</h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{note.content}</p>
                      {index < analysis.section_notes.length - 1 && <Separator className="mt-4" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Rewritten Content */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  AI-Optimized Content
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Summary</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg" data-testid="rewritten-summary">
                      {analysis.rewritten_summary}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2" data-testid="rewritten-skills">
                      {analysis.rewritten_skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Experience Bullets</h4>
                    <ul className="space-y-2" data-testid="rewritten-bullets">
                      {analysis.rewritten_bullets.map((bullet, index) => (
                        <li key={index} className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg flex gap-2">
                          <span className="text-emerald-500 font-bold">•</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Final Resume Draft */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-800 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Export-Ready Resume
                </h3>
                
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>📋 How to use:</strong> Click "Copy Resume" button above or select all text below, copy it, and paste into your document editor (Word, Google Docs, etc.) to create your PDF.
                  </p>
                </div>

                <pre className="mono text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-6 rounded-lg overflow-x-auto whitespace-pre-wrap" data-testid="final-resume-draft">
                  {analysis.final_resume_draft}
                </pre>
              </div>

              {/* New Analysis Button */}
              <div className="text-center">
                <Button
                  data-testid="new-analysis-button"
                  onClick={() => {
                    setAnalysis(null);
                    setResumeText('');
                    setJobDescription('');
                  }}
                  className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 rounded-full px-8 py-4 text-lg font-semibold"
                >
                  Start New Analysis
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
